import { Answer, QuestionResults, Section } from "@/types/resources";

import { countUniqueRespondents } from "./_respondentCount";

/* ── Shared cooker ─────────────────────────────────────────
   Use cases that share the "winner + ranking + heatmap" layout
   (Monadic, ClaimTest, AdCreative, Packaging) all derive their
   report data the same way: the section's rotation cells become the
   entity axis, the templated scoring battery becomes the metric axis,
   and per-cell answers fold into top-box counts. The chrome differs
   between use cases (concept vs claim vs ad vs pack) but the cooking
   is identical, so each per-use-case cooker just supplies its own
   translated metric labels + entity label formatter and renames the
   entity field for its `<UseCase>ReportData` shape. */

export interface TopBoxScoring {
  entities: { id: string; name: string }[];
  metrics: { id: string; label: string }[];
  perCellSampleSize: number[];
  /** Distinct respondents across the whole section, deduped on
      `persona.id`. Use this — not `sum(perCellSampleSize)` — for any
      "N respondents" label, since rotated sections show each
      respondent in multiple cells. */
  respondentCount: number;
  topBoxCounts: number[][]; // [entityIdx][metricIdx]
}

/**
 * Decide whether a single answer counts as a "top-box" response.
 *
 * - `linear_scale` / `rating_scale`: top-2-box, i.e. rating ≥ max-1
 *   where max = `question.options.length`. For 5-pt Likert that's
 *   4 or 5; for 7-pt it's 6 or 7.
 * - `nps`: rating ≥ 9 (promoters in canonical NPS banding).
 * - `single_choice` / `multiple_choice`: option 0 is the top option
 *   by convention (Likert-style: "Strongly agree" first).
 */
function isTopBox(answer: Answer["answer"], scaleSize: number): boolean {
  switch (answer.type) {
    case "linear_scale":
    case "rating_scale":
      return answer.rating !== null && answer.rating >= scaleSize - 1;
    case "nps":
      return answer.rating !== null && answer.rating >= 9;
    case "single_choice":
      return answer.choice === 0;
    case "multiple_choice":
      return Array.isArray(answer.choices) && answer.choices.includes(0);
    default:
      return false;
  }
}

const SCORING_QUESTION_TYPES = new Set([
  "linear_scale",
  "rating_scale",
  "nps",
  "single_choice",
  "multiple_choice",
]);

/** Filters the section's questions down to the ones a "top-box"
    report scores against — anything that produces a numeric rating
    or a Likert-style choice. */
function pickScoringMetrics(results: QuestionResults[]): QuestionResults[] {
  return (results ?? []).filter((r) =>
    r?.question?.type ? SCORING_QUESTION_TYPES.has(r.question.type) : false
  );
}

/**
 * Build a `TopBoxScoring` payload from the section's rotation cells, the
 * flat list of question results, and a list of pre-translated
 * metric labels supplied by the caller.
 *
 * One entity per authored cell — `entityLabel(idx)` names it (e.g.
 * "Pack 1"/"Ad 1"); answers carry the same 0-based `cell` index. Labels
 * are intentionally positional rather than pulled from a cell variable:
 * the variable values are surfaced inside the detailed question text.
 *
 * `metricLabels[i]` is the label for the i-th scoring question in
 * the section (templated order). When the section has more scoring
 * questions than the caller provided labels for, extra questions
 * are dropped so the heatmap can't sprout untranslated rows.
 *
 * Per-cell sample size is counted off the first scoring metric to
 * avoid double-counting (every persona who saw a concept answers
 * every metric in the battery, so the first metric is a faithful
 * 1:1 proxy for "respondents who saw this concept").
 */
export function cookTopBoxScoring(
  data: QuestionResults[] | undefined,
  section: Section | undefined,
  metricLabels: string[],
  entityLabel: (index: number) => string
): TopBoxScoring {
  const entities = (section?.cells ?? []).map((_, idx) => ({
    id: String(idx),
    name: entityLabel(idx),
  }));

  const scoringMetrics = pickScoringMetrics(data ?? []).slice(
    0,
    metricLabels.length
  );
  const metrics = scoringMetrics.map((r, idx) => ({
    id: r.question.id,
    label: metricLabels[idx],
  }));

  const topBoxCounts: number[][] = entities.map(() => metrics.map(() => 0));
  const perCellSampleSize: number[] = entities.map(() => 0);

  scoringMetrics.forEach((r, metricIdx) => {
    const scaleSize = r.question.options?.length ?? 0;

    (r.groups ?? []).forEach((g) => {
      (g.answers ?? []).forEach((a) => {
        const conceptIdx = a.cell;
        if (
          conceptIdx === null ||
          conceptIdx === undefined ||
          conceptIdx < 0 ||
          conceptIdx >= entities.length
        ) {
          return;
        }
        if (metricIdx === 0) {
          perCellSampleSize[conceptIdx] += 1;
        }
        if (isTopBox(a.answer, scaleSize)) {
          topBoxCounts[conceptIdx][metricIdx] += 1;
        }
      });
    });
  });

  return {
    entities,
    metrics,
    perCellSampleSize,
    respondentCount: countUniqueRespondents(data),
    topBoxCounts,
  };
}
